import { InlineAlert } from '../../components/BaseComponents'
import { ReactNode, useEffect, useState } from "react";
import { Button, LoadingSpinner, SearchableSelect, TextArea } from "../../components/BaseComponents";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import CurrencyInput from "../../components/BaseComponents/Forms/CurrencyInput";
import Modal from "../../components/BaseComponents/Modal/Modal";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import useApi from "../../hooks/useApi";
import { currencyToCents, getCSRFToken } from "../../lib/utils";
import { CurrencyCode } from "../../types/currency";

type PortfolioPcafAllocationModalProps = {
    onClose: () => void,
    onSaved: () => void,
    isOpen?: boolean,
    portfolioId: string,
    companyId: string,
    companyName: string,
};

// Renders **bold** and *italic* markdown spans from docs/todo/pcaf-help-for-10-categories.md as JSX.
function renderInlineMarkdown(text: string): ReactNode {
    return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={index}>{part.slice(1, -1)}</em>;
        }
        return part;
    });
}

// PCAF instrument categories per the 2025 Financed Emissions Standard (Part A) - see
// docs/todo/pcaf-emissions-allocation-plan.md section 4. Categories 8-10 are listed so the
// full taxonomy is visible, but disabled and deferred to a later release.
// denominatorLabel is one entry per display line (see docs/todo/pcaf-allocation-ui-tweaks.md 2.8) -
// the stored/API denominatorType joins these lines back into a single string.
// helpText is one entry per bullet point, transcribed from docs/todo/pcaf-help-for-10-categories.md
// (PCAF 2025 3rd Edition guidance), rendered under a shared "Summary of PCAF guidance" heading -
// the source doc's per-category heading is omitted since the dropdown selection already makes it clear.
const INSTRUMENT_CATEGORIES: { value: string, denominatorLabel: string[], helpText: string[], disabled?: boolean }[] = [
    {
        value: 'Listed equity and corporate bonds',
        denominatorLabel: ['EVIC (enterprise value including cash)'],
        helpText: [
            'EVIC: use **market cap of shares** + **total debt** (all instruments incl. preferred shares + minority interests) with no deduction of cash or equivalent',
            'Do *not* use **book equity** or **purchase price**. EVIC uses **market cap** as the equity component.',
            '**Outstanding amount**: for equity, use current market value of holding; for bonds, outstanding principal/book value held (not purchase price or fair value)',
            'Attribution factor = **bank\'s holding** ÷ **EVIC** and applies identically to both bonds and equity.',
            'When a financial institution holds both bonds *and* equity in the same issuer, calculate and report them separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Business loans and unlisted equity',
        denominatorLabel: ['Total equity + total debt (alternatively, use total assets instead of equity + debt)'],
        helpText: [
            'Denominator: use **total book value of equity** + **debt** of the borrower (no EVIC, no market cap. Unlisted companies have no observable market value for equity).',
            'Do *not* substitute EVIC or market cap for the denominator. Private companies require book equity + book debt instead.',
            '**Outstanding amount** (loans): use actual outstanding loan balance (disbursed amount minus repayments), *not* committed facility/limit amount.',
            '**Outstanding amount** (unlisted equity): calculated as the institution\'s % share of total shares × total equity per balance sheet, not purchase price or fair value.',
            'Attribution factor = **outstanding amount** ÷ (**total equity** + **total debt**). This applies identically whether the exposure is a loan or an unlisted equity stake.',
            'If total company equity is *negative* per the balance sheet, floor it to zero. All emissions are then attributed to debt only, none to equity.',
            'When holding both a loan *and* equity in the same unlisted entity, calculate and report each exposure separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Project finance',
        denominatorLabel: ['Total equity + total debt of the project'],
        helpText: [
            'Denominator: use **total project equity** + **debt** available to the project. This is the total financing structure, *not* the project\'s asset/book value.',
            'At project start, the denominator is total financing committed; once operational, use the *actual* equity and debt drawn to date, not the original committed amount.',
            '**Outstanding amount**: the bank\'s actual outstanding loan/equity contribution to the project (not committed/undrawn facility).',
            'Attribution factor = **bank\'s outstanding amount** ÷ (**total project equity** + **total project debt**).',
            'Report Scope 1 and Scope 2 of the project as *absolute*, required figures; Scope 3 is reported separately and is *not* combined into a single blended total.',
            'Where a bank holds *both* a loan and equity in the same project, calculate and report each separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Commercial real estate',
        denominatorLabel: ['Property value at origination'],
        helpText: [
            'Denominator: use **property valuation at origination** (or latest valuation if origination data unavailable), *not* latest/current market valuation.',
            '**Outstanding amount** = loan balance at reporting date (do *not* use original committed facility or undrawn amount).',
            'Attribution factor = **bank\'s outstanding loan** ÷ **property value at origination**. Applies per property/per SPV loan.',
            'For single-property SPVs, shareholder equity on the SPV balance sheet is *not* part of the attribution denominator.',
            'Benchmark energy intensity data (e.g. kWh/m² comparator databases) should *not* be used when actual metered consumption data is available.',
            'Total lettable floor area is only relevant where it feeds Scope 3 estimation methodology (e.g. embodied CO₂e per m²), not as Scope 1/2 data.',
        ],
    },
    {
        value: 'Mortgages',
        denominatorLabel: ['Property value at origination'],
        helpText: [
            'Denominator: use **property value at origination** fixed for the life of the loan and *not* re-marked when house prices rise or fall.',
            'Only use current/latest valuation as a substitute if the origination value genuinely cannot be sourced.',
            '**Outstanding amount** = actual outstanding mortgage balance at reporting date, *not* the original loan amount or credit limit.',
            'Attribution factor = **outstanding mortgage balance** ÷ **property value at origination**.',
            'Emissions factors should be selected by property type and location; use EPC rating/energy label where available rather than a generic national average.',
            'Do *not* use benchmark/comparator energy intensity data where actual metered consumption exists (same principle as commercial real estate).',
        ],
    },
    {
        value: 'Motor vehicle loans',
        denominatorLabel: ['Vehicle value at origination'],
        helpText: [
            'Denominator: use **total value of the vehicle at loan origination** (the purchase price at the time of the transaction), *not* current resale/market value.',
            '**Outstanding amount** = the actual outstanding motor vehicle loan balance owed by the debtor, *not* the original loan amount.',
            'Attribution factor = **outstanding loan amount** ÷ **vehicle value at origination**.',
            'Emissions should be calculated from vehicle-specific data (fuel type, make/model) combined with estimated or actual annual distance driven. Use *actual* mileage where telematics/odometer data exists, in preference to national average mileage assumptions.',
            'For loan books with many vehicles, an even/average allocation approach *may* be used only where individual vehicle-level data is genuinely unavailable. This is a fallback, not the preferred method.',
        ],
    },
    {
        value: 'Use of proceeds structures',
        denominatorLabel: ['Total equity + total debt of the financed asset'],
        helpText: [
            'Applies to green bonds, sustainability-linked loans, and any instrument where proceeds are explicitly ring-fenced to a specific project or use and *not* general corporate purpose lending.',
            'Denominator switches from the issuer\'s aggregate EVIC/book value to the **total cost** of the specific financed project/object (e.g. total capex).',
            '**Outstanding amount** = the bank\'s exposure specifically allocated to that project/use, *not* the issuer\'s total outstanding debt.',
            'Object-value attribution factor = **outstanding loan amount allocated to project** ÷ **total project cost**. This is calculated *per financed object*, then aggregated.',
            'Distinguish *separate* structures (financed emissions arise only once funds are actually allocated) from *integrated* structures (both allocated and unallocated amounts must be considered).',
            'Where proceeds cannot be traced to a specific use, treat the exposure under the general corporate methodology for debt or equity instead, *not* under use-of-proceeds rules.',
        ],
    },
    { value: 'Securitization and structured products', denominatorLabel: [], helpText: [], disabled: true },
    { value: 'Sovereign debt', denominatorLabel: [], helpText: [], disabled: true },
    { value: 'Sub-sovereign debt', denominatorLabel: [], helpText: [], disabled: true },
]

const INSTRUMENT_CATEGORY_OPTIONS = INSTRUMENT_CATEGORIES.map((category, index) => ({
    value: category.value,
    label: `${index + 1}. ${category.value}${category.disabled ? ' (supported in a later release)' : ''}`,
    disabled: !!category.disabled,
}))

function PortfolioPcafAllocationModal({
    onClose,
    onSaved,
    isOpen,
    portfolioId,
    companyId,
    companyName,
}: PortfolioPcafAllocationModalProps) {
    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('GBP');
    const [instrumentCategory, setInstrumentCategory] = useState('');
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [denominatorAmount, setDenominatorAmount] = useState(0);
    const [methodologyNotes, setMethodologyNotes] = useState('');
    const [savedAttributionFactor, setSavedAttributionFactor] = useState(1);
    const [showHelp, setShowHelp] = useState(false);
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        const run = async () => {
            const result = await api.get(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`);
            if (cancelled) {
                return;
            }
            const { data } = result;
            setCurrencyCode(data.currencyCode);
            setInstrumentCategory(data.instrumentCategory || '');
            setOutstandingAmount(Number(data.outstandingAmount) || 0);
            setDenominatorAmount(Number(data.denominatorAmount) || 0);
            setMethodologyNotes(data.methodologyNotes || '');
            setSavedAttributionFactor(data.attributionFactor);
            setLoading(false);
        }
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, portfolioId, companyId]);

    const selectedCategory = INSTRUMENT_CATEGORIES.find(category => category.value === instrumentCategory);
    const isValid = !!instrumentCategory && denominatorAmount > 0 && outstandingAmount >= 0;

    // Bad/incomplete input never corrupts the display - it just keeps showing the saved value.
    const attributionFactor = isValid ? outstandingAmount / denominatorAmount : savedAttributionFactor;

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            await api.put(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`, {
                instrumentCategory,
                outstandingAmount,
                denominatorType: selectedCategory?.denominatorLabel.join(' '),
                denominatorAmount,
                methodologyNotes: methodologyNotes || undefined,
                _csrf: await getCSRFToken(),
            });
            onSaved();
        } catch (e) {
            setError('Could not save this allocation. Please check your inputs and try again.');
        } finally {
            setSaving(false);
        }
    }

    const removeAllocation = async () => {
        setRemoveConfirmOpen(false);
        setSaving(true);
        setError('');
        try {
            await api.delete(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`, {
                _csrf: await getCSRFToken(),
            });
            onSaved();
        } catch (e) {
            setError('Could not remove this allocation. Please try again.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <Modal open={!!isOpen} onClose={onClose} size="small">
                <div className="flex flex-col">
                    <PageHeadings title="Allocate emissions" />
                    <div className="block text-charcoal-100">{companyName}</div>
                    { loading || saving ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            { instrumentCategory && selectedCategory && (
                                <div className="rounded-lg bg-sun-500 p-4 mt-8">
                                    <div className={`rounded-lg bg-white p-4 mb-4 text-charcoal-800 text-sm ${!showHelp && 'hidden'}`} id="pcafHelpSection" data-testid="pcaf-help-section">
                                        <p className="mb-2">Summary of PCAF guidance (2025, 3rd Edition)</p>
                                        <ul className="list-disc pl-5 space-y-1">
                                            { selectedCategory.helpText.map((line, index) => (
                                                <li key={index}>{renderInlineMarkdown(line)}</li>
                                            )) }
                                        </ul>
                                    </div>
                                    <Button
                                        type="Primary"
                                        text={showHelp ? 'Hide information' : 'What information is required?'}
                                        hasLeadingIcon
                                        size="sm"
                                        icon={`fa-solid ${showHelp ? 'fa-minus' : 'fa-plus'}`}
                                        onClick={() => setShowHelp(!showHelp)}
                                        ariaControls="pcafHelpSection"
                                        ariaExpanded={showHelp}
                                    />
                                </div>
                            )}

                            <label className="mt-8 block text-charcoal-100">
                                PCAF instrument category
                                <SearchableSelect
                                    className="mt-1"
                                    fillContainer
                                    aria-label="PCAF instrument category"
                                    isSearchable={false}
                                    placeholder="Select category..."
                                    isOptionDisabled={(option) => !!option.disabled}
                                    options={INSTRUMENT_CATEGORY_OPTIONS}
                                    value={INSTRUMENT_CATEGORY_OPTIONS.find((option) => option.value === instrumentCategory)}
                                    onChange={(option) => {
                                        setInstrumentCategory(option?.value ?? '');
                                        setShowHelp(false);
                                    }}
                                />
                            </label>

                            { instrumentCategory && (
                                <>
                                    <label className="mt-4 block text-charcoal-100" data-testid="pcaf-outstanding-amount">
                                        Outstanding amount
                                        <CurrencyInput
                                            name="outstandingAmount"
                                            currencyCode={currencyCode}
                                            defaultValue={outstandingAmount}
                                            onChange={value => setOutstandingAmount(currencyToCents(value))}
                                        />
                                    </label>
                                    <label className="mt-4 block text-charcoal-100" data-testid="pcaf-denominator-amount">
                                        { selectedCategory?.denominatorLabel.map(line => (
                                            <span className="block" key={line}>{line}</span>
                                        )) || 'Denominator amount' }
                                        <CurrencyInput
                                            name="denominatorAmount"
                                            currencyCode={currencyCode}
                                            defaultValue={denominatorAmount}
                                            onChange={value => setDenominatorAmount(currencyToCents(value))}
                                        />
                                    </label>

                                    <div className="mt-4 text-charcoal-100">
                                        Attribution factor: <span>{ (attributionFactor * 100).toFixed(2) }%</span>
                                    </div>

                                    <label className="mt-4 block text-charcoal-100">
                                        Methodology notes (optional)
                                        <TextArea
                                            className="mt-1"
                                            value={methodologyNotes}
                                            onChange={e => setMethodologyNotes(e.target.value)}
                                            placeholder="Add comments, assumptions, or other relevant information."
                                        />
                                    </label>
                                </>
                            )}

                            { error && (
                                <InlineAlert type='error' className='mt-6'>{ error }
                                </InlineAlert>
                            )}

                            <div className="mt-8 grow">
                                <Button onClick={save} additionalClass="mr-5" disabled={!isValid} type="Primary" text="Save" />
                                <Button onClick={() => setRemoveConfirmOpen(true)} additionalClass="mr-5" type="Outlined" text="Remove allocation" />
                                <Button onClick={onClose} type="Outlined" text="Cancel" />
                            </div>
                        </>
                    )}
                </div>
            </Modal>
            <ConfirmationModal
                open={removeConfirmOpen}
                onCancel={() => setRemoveConfirmOpen(false)}
                onConfirm={removeAllocation}
            >
                <p className="text-white my-8">Remove this allocation and revert to default?</p>
            </ConfirmationModal>
        </>
    )
}

export default PortfolioPcafAllocationModal;
